import { afterNextRender, DestroyRef, Directive, ElementRef, inject, input } from '@angular/core';

import { Clock } from '../runtime/clock/clock';
import { PresentationController } from './presentation-controller';

const FRAME_MS = 16;

@Directive({ selector: '[dsScrollContent]' })
export class ScrollContentDirective {
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly clock = inject(Clock);
  private readonly destroyRef = inject(DestroyRef);
  private readonly abort = new AbortController();
  private release?: () => void;
  private started = false;

  public readonly dsScrollContent = input.required<PresentationController>();
  public readonly rowSteps = input(false);

  public constructor() {
    afterNextRender(() => {
      // The hold is taken before any measurement so a short slide cannot finish while layout is still settling.
      this.takeHold();
      const observer = new ResizeObserver(() => this.measure());
      observer.observe(this.element.nativeElement);
      this.measure();
      this.destroyRef.onDestroy(() => {
        observer.disconnect();
        this.abort.abort();
        this.releaseHold();
      });
    });
  }

  private takeHold(): void {
    this.release ??= this.dsScrollContent().hold();
  }

  private releaseHold(): void {
    this.release?.();
    this.release = undefined;
  }

  private measure(): void {
    const element = this.element.nativeElement;

    if (this.started) {
      return;
    }

    if (element.scrollHeight <= element.clientHeight + 2) {
      this.releaseHold();

      return;
    }

    this.started = true;

    // Content can start overflowing only once a late image or font lands, by which time the first hold is gone.
    this.takeHold();

    const controller = this.dsScrollContent();
    void this.scroll(element, AbortSignal.any([controller.signal, this.abort.signal]))
      .catch((): void => undefined).finally((): void => this.releaseHold());
  }

  private async scroll(element: HTMLElement, signal: AbortSignal): Promise<void> {
    await this.clock.after(5000, signal);
    const rows = this.rowSteps() ? Array.from(element.querySelectorAll('tbody tr')) : [];
    const steps = rows.length ? rows.map(row => row.getBoundingClientRect().height) : [element.scrollHeight];

    for (const step of steps) {
      const start = element.scrollTop;
      const target = Math.min(element.scrollHeight - element.clientHeight, start + step);
      const duration = Math.max(FRAME_MS, (target - start) * (rows.length ? 7.7 : 55));
      const begins = this.clock.now().toEpochMilli();

      // Driven by elapsed time, never by reading scrollTop back: a clamped or rounded read would never reach the target.
      for (let elapsed = 0; !signal.aborted && elapsed < duration;) {
        await this.clock.after(FRAME_MS, signal);
        elapsed = this.clock.now().toEpochMilli() - begins;
        element.scrollTop = start + (target - start) * Math.min(1, elapsed / duration);
      }

      if (rows.length) {
        await this.clock.after(500, signal);
      }
    }

    await this.clock.after(2000, signal);
  }
}
